"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useUserDisplayName = exports.useProjectUsersAsOptions = exports.useMemberDropdownOptionByOxygenId = exports.useGroupedProjectMembersAsOptions = exports.useGetLevelOneUserShouldIncludeMemberFn = exports.getLevelOneUserShouldIncludeMemberFn = void 0;
var _useSelectedOption = require("../../utils/hooks/useSelectedOption");
var _react = require("react");
var _sdk = require("../../../sdk");
var _oxygenUtils = require("../../utils/data/oxygenUtils");
var _lodash = require("lodash");
var _projectMembersHooks = require("../../utils/hooks/projectMembersHooks");
var _alloyHooks = require("../../utils/hooks/alloyHooks");
var _useTranslations = require("../../utils/i18n/useTranslations");
var _featureFlags = require("../../utils/featureFlags");
const defaultShouldIncludeMemberFn = (0, _lodash.constant)(true);
const getLevelOneUserShouldIncludeMemberFn = _ref => {
  let {
    currentUserOxygenId,
    currentUserCompanyId,
    currentUserCompanyOxygenId,
    usersEntitiesMap
  } = _ref;
  return option => {
    if (!currentUserOxygenId) {
      return false;
    }
    if (option.memberType === _sdk.ProjectMemberType.User) {
      if (option.value === currentUserOxygenId) {
        return true;
      }
      if (!!currentUserCompanyId) {
        const userOption = usersEntitiesMap[option.value] || {};
        return userOption.companyId === currentUserCompanyId;
      }
    } else if (option.memberType === _sdk.ProjectMemberType.Company && !!currentUserCompanyOxygenId) {
      return option.value === currentUserCompanyOxygenId;
    }
    return false;
  };
};
exports.getLevelOneUserShouldIncludeMemberFn = getLevelOneUserShouldIncludeMemberFn;
const useGetLevelOneUserShouldIncludeMemberFn = () => {
  var _ref2, _companiesEntitiesMap;
  const {
    user: currentUser
  } = (0, _alloyHooks.useUser)();
  const currentUserOxygenId = currentUser && currentUser.autodeskId ? currentUser.autodeskId : null;
  const usersEntitiesMap = (0, _projectMembersHooks.useUsersEntitiesMapByOxygenId)();
  const companiesEntitiesMap = (0, _projectMembersHooks.useCompaniesEntitiesMapById)();
  const currentUserCompanyId = currentUserOxygenId && usersEntitiesMap[currentUserOxygenId] ? usersEntitiesMap[currentUserOxygenId].companyId : null;
  const currentUserCompanyOxygenId = (_ref2 = currentUserCompanyId && ((_companiesEntitiesMap = companiesEntitiesMap[currentUserCompanyId]) === null || _companiesEntitiesMap === void 0 ? void 0 : _companiesEntitiesMap.memberGroupId)) !== null && _ref2 !== void 0 ? _ref2 : null;
  return getLevelOneUserShouldIncludeMemberFn({
    currentUserOxygenId,
    currentUserCompanyId,
    currentUserCompanyOxygenId,
    usersEntitiesMap
  });
};
exports.useGetLevelOneUserShouldIncludeMemberFn = useGetLevelOneUserShouldIncludeMemberFn;
const useProjectUsersAsOptions = function () {
  let {
    shouldIncludeMemberFn = defaultShouldIncludeMemberFn
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    data: users
  } = (0, _projectMembersHooks.useUsersWrapper)();
  const {
    data: companies
  } = (0, _projectMembersHooks.useCompaniesWithoutDeletedCompanies)();
  const companyIdToNameMap = (0, _oxygenUtils.useCompanyIdToNameMap)(companies);
  const getNameCountInCompany = (0, _oxygenUtils.useGetNameCountInCompany)(users);
  return (0, _react.useMemo)(() => users.filter(user => user.autodeskId).map(user => {
    return {
      label: "".concat(user.name).concat(getNameCountInCompany({
        userName: user.name,
        userCompanyId: user.companyId
      }) > 1 ? " - ".concat(user.email) : ""),
      subtext: companyIdToNameMap[user.companyId],
      value: user.autodeskId,
      memberType: _sdk.ProjectMemberType.User
    };
  }).filter(shouldIncludeMemberFn), [users, companyIdToNameMap, getNameCountInCompany, shouldIncludeMemberFn]);
};
exports.useProjectUsersAsOptions = useProjectUsersAsOptions;
const useGroupedProjectMembersAsOptions = function () {
  let {
    shouldIncludeMemberFn = defaultShouldIncludeMemberFn,
    shouldIncludeEmpty = false
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    data: users
  } = (0, _projectMembersHooks.useUsersWrapper)();
  const {
    data: roles
  } = (0, _projectMembersHooks.useRolesWithoutInactiveRoles)();
  const {
    data: companies
  } = (0, _projectMembersHooks.useCompaniesWithoutDeletedCompanies)();
  const companyIdToNameMap = (0, _oxygenUtils.useCompanyIdToNameMap)(companies);
  const getNameCountInCompany = (0, _oxygenUtils.useGetNameCountInCompany)(users);
  const {
    T
  } = (0, _useTranslations.useTranslations)();
  const {
    value: shouldHideEmptyRolesAndCompanies
  } = (0, _featureFlags.useFeatureFlag)(_featureFlags.FeatureFlag.HideEmptyRolesAndCompanies);

  //If the FF is off then all uses of this function should include the empty roles and companies
  if (!shouldHideEmptyRolesAndCompanies) {
    shouldIncludeEmpty = true;
  }
  const usedRoles = (0, _react.useMemo)(() => {
    const uniqRoleIds = new Set();
    users.forEach(user => {
      user.roleIds.forEach(roleId => {
        uniqRoleIds.add(roleId);
      });
    });
    return uniqRoleIds;
  }, [users]);
  const formattedUsers = (0, _react.useMemo)(() => {
    return {
      label: T.translate("projectMemberTypes.user"),
      options: users.filter(user => user.autodeskId).map(user => ({
        label: "".concat(user.name || user.email).concat(getNameCountInCompany({
          userName: user.name,
          userCompanyId: user.companyId
        }) > 1 ? " - ".concat(user.email) : ""),
        subtext: companyIdToNameMap[user.companyId],
        value: user.autodeskId,
        memberType: _sdk.ProjectMemberType.User
      })).filter(shouldIncludeMemberFn)
    };
  }, [T, companyIdToNameMap, getNameCountInCompany, shouldIncludeMemberFn, users]);
  const formattedRoles = (0, _react.useMemo)(() => {
    return {
      label: T.translate("projectMemberTypes.role"),
      options: roles.filter(role => role.memberGroupId && shouldIncludeEmpty ? true : usedRoles.has(role.id)).map(role => ({
        label: role.name,
        value: role.memberGroupId,
        memberType: _sdk.ProjectMemberType.Role
      })).filter(shouldIncludeMemberFn)
    };
  }, [T, roles, shouldIncludeEmpty, shouldIncludeMemberFn, usedRoles]);
  const formattedCompanies = (0, _react.useMemo)(() => {
    return {
      label: T.translate("projectMemberTypes.company"),
      options: companies.filter(company => company.memberGroupId && shouldIncludeEmpty ? true : company.memberCount > 0).map(company => ({
        label: company.name,
        value: company.memberGroupId,
        memberType: _sdk.ProjectMemberType.Company
      })).filter(shouldIncludeMemberFn)
    };
  }, [T, companies, shouldIncludeEmpty, shouldIncludeMemberFn]);
  return (0, _react.useMemo)(() => {
    return [formattedUsers, formattedRoles, formattedCompanies];
  }, [formattedUsers, formattedRoles, formattedCompanies]);
};
exports.useGroupedProjectMembersAsOptions = useGroupedProjectMembersAsOptions;
const useMemberDropdownOptionByOxygenId = oxygenId => {
  const groupedOptions = useGroupedProjectMembersAsOptions({
    shouldIncludeEmpty: true
  });
  const option = (0, _useSelectedOption.useSelectedOption)(oxygenId, groupedOptions);
  return option || undefined;
};
exports.useMemberDropdownOptionByOxygenId = useMemberDropdownOptionByOxygenId;
const useUserDisplayName = oxygenId => {
  const {
    T
  } = (0, _useTranslations.useTranslations)();
  const selectedOption = useMemberDropdownOptionByOxygenId(oxygenId);
  return (0, _react.useMemo)(() => {
    if (selectedOption) {
      return "".concat(selectedOption.label).concat(selectedOption.subtext ? " (".concat(selectedOption.subtext, ")") : "");
    }
    // if we did not find a selected option but the issue is already assigned to someone, this means the assignee is a former member.
    // currently, the existing options are only for active members.
    if (oxygenId) {
      return T.translate("removedMember");
    }
    return undefined;
  }, [T, oxygenId, selectedOption]);
};
exports.useUserDisplayName = useUserDisplayName;
//# sourceMappingURL=common.js.map